import { Icon } from "@mdi/react";
import { useCallback, useEffect, useState } from "react";

import { mdiAlert, mdiDelete, mdiPlus } from "@mdi/js";
import { Button } from "antd";
import mapImg from "assets/images/map-preview.png";
import _debounce from "lodash/debounce";
import styled from "styled-components";
import formatPropertyName from "utils/column/formatPropertyName";
import getColumnLabelWithContext, {
  getCompactFieldDisplayTitle,
  getFlatDisplayTitle
} from "utils/column/getColumnLabelWithContext";

import { useColumns, useGlobalProductionSettings } from "hooks";

import { Tooltip } from "components/base";
import { GeomBinProvider } from "components/geom-bin/contexts/GeomBinContext";
import GroupBy from "components/groupBy";
import {
  shapefileColumnFromKey,
  useIsDeletedShapefileField,
  useIsShapefileFieldAbsentFromProject,
  useResolveShapefileColumn
} from "components/project/shapefiles/hooks/useResolveShapefileColumn";
import { useResolvedDefaultFieldList } from "components/user-settings/hooks";
import { DefaultMapTooltipFields } from "components/user-settings/models";
import { useUserContext } from "components/user/context";
import { useUserDefaults, useUserSettingMutation } from "components/user/hooks";

import { useFacilityColumns } from "../../../../hooks/useColumns";
import { EntityKind } from "../../../../models/entityKind";
import {
  updateSettingFacilityMapTooltipFields,
  updateSettingMapTooltipFields
} from "../../../user/context/reducer/UserReducer";
import {
  DEFAULT_FACILITY_MAP_TOOLTIP_FIELDS_SETTING,
  DEFAULT_MAP_TOOLTIP_FIELDS_SETTING
} from "../../constants/userSetting.constants";
import {
  ErrorContainer,
  SectionActionsContainer,
  SectionContainer,
  SectionHeading,
  SectionHeadingTitleContainer,
  SectionInputWrapper,
  StyledSpinner
} from "./shared";

interface DefaultMapTooltipFieldsProps {
  entityKind: EntityKind;
}

const DefaultMapTooltipFieldSetting = ({ entityKind }: DefaultMapTooltipFieldsProps) => {
  // hooks
  const [, dispatch] = useUserContext();
  const defaults = useUserDefaults();
  const mutation = useUserSettingMutation<DefaultMapTooltipFields>(
    entityKind == EntityKind.Well
      ? DEFAULT_MAP_TOOLTIP_FIELDS_SETTING
      : DEFAULT_FACILITY_MAP_TOOLTIP_FIELDS_SETTING
  );
  const columns = useColumns();
  const facilityColumns = useFacilityColumns();
  const resolveShapefileColumn = useResolveShapefileColumn();
  const isDeletedShapefileField = useIsDeletedShapefileField();
  const isAbsentShapefileField = useIsShapefileFieldAbsentFromProject();
  const globalSettings = useGlobalProductionSettings(EntityKind.Well);
  const columnsToUse = entityKind === EntityKind.Well ? columns : facilityColumns;
  const isLoading = columnsToUse.length === 0;

  // state
  const [updateError, setUpdateError] = useState("");
  const defaultFields = useCallback(() => {
    return entityKind === EntityKind.Well
      ? defaults?.mapTooltipFields
      : defaults?.facilityMapTooltipFields;
  }, [entityKind, defaults]);
  const fields = defaultFields()?.fields ?? [];

  // Fields that don't currently resolve to a live column - either the field
  // was deleted, or it's just out of context (e.g. a project-scoped field
  // with no project selected). Excluded from the max-count logic below so a
  // deleted field can't permanently occupy a slot, but never auto-persisted
  // - see useResolvedDefaultFieldList for why.
  const resolvedFields = useResolvedDefaultFieldList({
    rawList: fields,
    columnsToUse,
    isLoading,
    resolveFallbackColumn: resolveShapefileColumn,
    isAbsentField: isAbsentShapefileField
  });

  const handleDefaultFieldsChange = useCallback(
    (index: number, propertyName: string) => {
      if ((defaultFields().fields || []).includes(propertyName)) {
        setUpdateError("Field already set as a default");
        return;
      }
      const newFields = [...(defaultFields().fields || [])];
      newFields[index] = propertyName;
      mutation.mutate({
        id: defaultFields()?.id,
        fields: newFields
      });
    },
    [defaultFields]
  );

  // Handle reload
  useEffect(() => {
    if (!mutation?.data?.id) return;

    setUpdateError("");
    if (entityKind === EntityKind.Well) {
      updateSettingMapTooltipFields(dispatch, {
        id: mutation.data.id,
        fields: mutation.data.fields
      });
    } else {
      updateSettingFacilityMapTooltipFields(dispatch, {
        id: mutation.data.id,
        fields: mutation.data.fields
      });
    }
  }, [mutation?.data]);

  // Handle reload
  useEffect(() => {
    if (mutation?.error) {
      // eslint-disable-next-line no-console
      console.error(mutation?.error);
      setUpdateError("Failed to update default map tooltip fields.");
    } else {
      setUpdateError("");
    }
  }, [mutation?.error]);

  const debouncedAddField = _debounce((settings: DefaultMapTooltipFields) => {
    mutation.mutate(settings);
  }, 400);

  const handleAddField = useCallback(() => {
    const focusFields = defaultFields();
    const next = columnsToUse.filter(
      (c) => !(focusFields?.fields || []).includes(c.property)
    )[0];
    const newFields = [...(focusFields?.fields || []), next.property];

    debouncedAddField({
      id: focusFields?.id,
      fields: newFields
    });
  }, [defaultFields]);

  const handleRemoveField = useCallback(
    (e: MouseEvent, index: number) => {
      const focusFields = defaultFields();
      const newFields = [...focusFields.fields];

      newFields.splice(index, 1);

      mutation.mutate({
        id: focusFields?.id,
        fields: newFields
      });

      e.stopPropagation();
    },
    [defaultFields]
  );

  return (
    <GeomBinProvider>
      <SectionContainer>
        <SectionHeading>
          <SectionHeadingTitleContainer>
            {entityKind} Map Tooltip Fields
            {updateError && (
              <ErrorContainer>
                <Icon path={mdiAlert} size={1} /> {updateError}
              </ErrorContainer>
            )}
          </SectionHeadingTitleContainer>
          {resolvedFields.length < 5 && (
            <SectionActionsContainer>
              <Button
                data-testid={`${entityKind}-default-focus-fields-add`}
                type="text"
                icon={<Icon path={mdiPlus} size={1} />}
                onClick={handleAddField}
                loading={mutation.isLoading}>
                Add
              </Button>
            </SectionActionsContainer>
          )}
        </SectionHeading>
        <SectionInputWrapper>
          {isLoading && <StyledSpinner />}
          {!isLoading && (
            <Grid data-testid={`${entityKind}-focus-fields`}>
              {fields.map((tile, index) => {
                const propertyName = formatPropertyName(tile);
                // Absence outranks the live column list: picking a shapefile
                // in group-by materializes its columns into the category list
                // and a project switch does not evict them, so a stale entry
                // could otherwise render as a healthy picker row for a field
                // this project's tooltip will never show.
                const column = isAbsentShapefileField(propertyName)
                  ? null
                  : columnsToUse.find((c) => propertyName === c.property) ??
                    resolveShapefileColumn(propertyName);

                // An entry with nothing behind it used to render nothing at
                // all, which took its delete button with it and left the field
                // stuck in the saved list forever - still requested by the map
                // tooltip, but with no way to reach it (EVA-126). Give it a row
                // whose only affordance is removal. The delete button ignores
                // the minimum-count gate below: a broken entry is never worth
                // keeping to satisfy a minimum.
                //
                // Two flavors, split by what the loaded tree can prove. A
                // shapefile deleted from this project reads "Unavailable
                // field"; one that is simply not in this project - the saved
                // list is global, so fields picked in other projects follow
                // the user here, where the tooltip filters them out - keeps
                // its field name and says where it belongs, since removing it
                // is optional cleanup, not repair. Anything the tree cannot
                // judge (still loading, or not a shapefile key at all) stays
                // hidden as before, rather than being offered for deletion
                // when nothing is wrong with it.
                if (!column) {
                  if (!isAbsentShapefileField(propertyName)) return;

                  const isDeletedHere = isDeletedShapefileField(propertyName);
                  return (
                    <UnavailableFieldRow
                      key={tile}
                      data-testid={`${entityKind}-default-focus-fields-unavailable-${index}`}>
                      <FieldTitle>
                        {isDeletedHere
                          ? "Unavailable field"
                          : shapefileColumnFromKey(propertyName)?.title ??
                            "Unavailable field"}
                      </FieldTitle>
                      <Tooltip
                        title={
                          isDeletedHere
                            ? "This field's shapefile is no longer in this project. Remove the field, or re-link the shapefile to restore it."
                            : "This field's shapefile belongs to another project, so it is not shown in this project's tooltip. Remove the field, or manage it in its own project."
                        }>
                        <StyledDeleteButton
                          type="text"
                          data-testid={`${entityKind}-default-focus-fields-delete-${index}`}
                          icon={<Icon path={mdiDelete} size={1.3} />}
                          onClick={(e) => handleRemoveField(e, index)}
                        />
                      </Tooltip>
                    </UnavailableFieldRow>
                  );
                }

                const containsFcst = tile.includes(".fcst");
                const label = getColumnLabelWithContext(
                  column,
                  !!globalSettings.normalizeBy,
                  null,
                  containsFcst,
                  undefined,
                  entityKind
                );
                return (
                  <GroupBy
                    key={tile}
                    excludedCategoryFilters={["Shapefile"]}
                    value={column}
                    entityKind={entityKind}
                    onChange={(groupBy) => {
                      handleDefaultFieldsChange(index, groupBy.property);
                    }}>
                    <StyledButton
                      data-testid={`${entityKind}-default-focus-fields-button-${index}`}>
                      <FieldTitle>
                        {getFlatDisplayTitle(column.property, label, column.title)}
                      </FieldTitle>
                      {resolvedFields.length > 2 && (
                        <Tooltip title="Remove focus tile">
                          <StyledDeleteButton
                            type="text"
                            data-testid={`${entityKind}-default-focus-fields-delete-${index}`}
                            icon={<Icon path={mdiDelete} size={1.3} />}
                            onClick={(e) => handleRemoveField(e, index)}
                          />
                        </Tooltip>
                      )}
                    </StyledButton>
                  </GroupBy>
                );
              })}
            </Grid>
          )}
        </SectionInputWrapper>
      </SectionContainer>

      <PreviewTooltipContainer className="preview-tooltip-container">
        <PreviewTooltip className="preview-tooltip">
          {fields.map((tile, index) => {
            const propertyName = formatPropertyName(tile);
            // Mirrors the real tooltip (useWellDetailHover): a field whose
            // shapefile is not in this project's tree - deleted, or picked in
            // another project - is dropped, a live shapefile field previews
            // under its name, and one the tree cannot yet judge is named from
            // the key itself rather than omitted. For that not-yet-judgeable
            // key this deliberately outruns the field list above, which
            // renders no row for it - a row needs a real column to back its
            // picker - while the preview's whole job is to show what the live
            // tooltip will actually do.
            if (isAbsentShapefileField(propertyName)) return;
            const column =
              columnsToUse.find((c) => propertyName === c.property) ??
              resolveShapefileColumn(propertyName) ??
              shapefileColumnFromKey(propertyName);

            if (!column) return;

            // Deliberately argument-for-argument identical to getColumnName:
            // no normalizeBy, forecast toggle OFF, entityKind in its own slot.
            // `isForecastToggleOn` is a real divergence, not a cosmetic one -
            // it rewrites the title through getCumPlusForecastTitle, and
            // lightContextTitle returns that title verbatim for Forecast and
            // Performance groups, so passing `tile.includes(".fcst")` here (as
            // the TILE above still does, being a picker rather than a preview)
            // previewed a `.fcst` field with a Cum + Forecast label the map
            // never renders.
            const label = getColumnLabelWithContext(
              column,
              false,
              null,
              false,
              undefined,
              entityKind
            );
            return (
              <PreviewGroup
                key={tile + " preview"}
                data-testid={`${entityKind}-default-focus-fields-preview-${index}`}>
                {/*
                  Named exactly as the live map tooltip names it
                  (`getColumnName` -> `getCompactFieldDisplayTitle`), so a custom
                  field previews as `Group: Field (unit)` with no category
                  prefix (EVA-127). The TILE above keeps the category via
                  `getFlatDisplayTitle` — it's a picker, where the category
                  tells same-named fields in different groups apart; only this
                  preview claims to show what the map will render.
                */}
                <span>{getCompactFieldDisplayTitle(column, label, propertyName)}:</span>
                <span> Example</span>
              </PreviewGroup>
            );
          })}
        </PreviewTooltip>
      </PreviewTooltipContainer>
    </GeomBinProvider>
  );
};

export default DefaultMapTooltipFieldSetting;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 5px;
`;

const StyledDeleteButton = styled(Button)`
  position: absolute;
  right: 0;
  top: 2px;
  padding: 0;
  color: #b5b5b5;
  opacity: 0;

  &:hover {
    color: var(--color-danger);
  }
`;

const StyledButton = styled(Button)`
  position: relative;
  width: 100%;
  background-color: rgba(var(--color-text-rgb), 0.05);
  color: var(--color-text);
  padding: 4px 24px;
  min-width: 0;

  &:hover {
    background-color: var(--color-text);
    color: #fff;

    ${StyledDeleteButton} {
      opacity: 1;
    }
  }
`;
const FieldTitle = styled.div`
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// A dead entry, kept on screen only so it can be removed. Muted, not a picker
// (there is nothing behind it to open one on), and its delete button stays
// visible rather than waiting for a hover the row's appearance doesn't invite.
const UnavailableFieldRow = styled(StyledButton)`
  color: rgba(var(--color-text-rgb), 0.5);
  font-style: italic;
  cursor: default;

  &:hover {
    background-color: rgba(var(--color-text-rgb), 0.05);
    color: rgba(var(--color-text-rgb), 0.5);
  }

  ${StyledDeleteButton} {
    opacity: 1;
  }
`;

const PreviewTooltipContainer = styled.div`
  background: url(${mapImg});
  background-size: auto;
  background-position: top left;
  width: 100%;
  padding: 24px;
  border-radius: var(--border-radius);
  border: 1px solid #d9d9d9;
`;

const PreviewTooltip = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: auto;
  min-width: 200px;
  max-width: 500px;
  border-radius: 10px;
  padding: 10px 15px;
  margin: 0 auto;
  background: radial-gradient(
    rgba(0, 0, 0, 0.2) 0%,
    rgba(0, 0, 0, 0.15) 80%,
    rgba(0, 0, 0, 0.1) 100%
  );
  backdrop-filter: blur(4px);
  box-shadow: var(--shadow-drawer);
  pointer-events: none;
`;

const PreviewGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: flex-end;
  gap: 2px 8px;

  span {
    font-size: 1.2rem;
    font-family: "Open Sans", sans-serif;
    min-height: 20px;
    line-height: 1.2;
    align-content: center;
    filter: drop-shadow(0 0 16px var(--color-surface-05));

    &:first-child {
      color: white;
      text-align: right;
    }

    &:last-child {
      color: #cdfffa;
      font-weight: bold;
    }
  }
`;
